import React, { useState } from 'react';
import { Button, Card, Checkbox, Col, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { COMPETENCY_PERMS as P, useCompetencyAction, useCompetencyCan, useTrainingCourses } from '@/hooks/useCompetency';
import { competencyService, type TrainingCourse } from '@/services/competency.service';
import CompetencyNav from './CompetencyNav';
import { COURSE_CATEGORIES, COURSE_FREQUENCIES } from './competencyDisplay';

const TrainingCoursesPage: React.FC = () => {
  const canManage = useCompetencyCan(P.trainingManage);
  const [category, setCategory] = useState<string>();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Partial<TrainingCourse> | null>(null);
  const [form] = Form.useForm();
  const { data = [], isLoading } = useTrainingCourses({ category, search });

  const save = useCompetencyAction(
    (v: Partial<TrainingCourse>) => competencyService.saveCourse(editing?.id ?? null, v),
    'Training course saved'
  );
  const remove = useCompetencyAction((id: string) => competencyService.deleteCourse(id), 'Training course deleted');

  const open = (c: Partial<TrainingCourse> | null) => {
    form.resetFields();
    form.setFieldsValue(
      c
        ? { ...c, is_induction: !!Number(c.is_induction), effectiveness_required: !!Number(c.effectiveness_required), is_active: !!Number(c.is_active) }
        : { category: 'QMS', frequency: 'One-time', effectiveness_required: true, is_active: true }
    );
    setEditing(c ?? {});
  };

  const columns: ColumnsType<TrainingCourse> = [
    { title: 'Code', dataIndex: 'code', width: 120 },
    { title: 'Training', dataIndex: 'title' },
    { title: 'Category', dataIndex: 'category', width: 160 },
    { title: 'Frequency', render: (_, r) => (r.frequency_months ? `${r.frequency} (${r.frequency_months} months)` : r.frequency || '-'), width: 170 },
    { title: 'Hours', dataIndex: 'duration_hours', width: 80, align: 'right' },
    {
      title: 'Flags',
      width: 200,
      render: (_, r) => (
        <Space size={4} wrap>
          {Number(r.is_induction) ? <Tag color="purple">Induction</Tag> : null}
          {Number(r.effectiveness_required) ? <Tag color="blue">Effectiveness check</Tag> : null}
          {!Number(r.is_active) ? <Tag>Inactive</Tag> : null}
        </Space>
      ),
    },
    {
      title: '',
      width: 90,
      render: (_, r) =>
        canManage ? (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => open(r)} />
            <Popconfirm title="Delete this course?" onConfirm={() => remove.mutate(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ) : null,
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <CompetencyNav
        title="Training courses"
        extra={
          canManage ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => open(null)}>
              New course
            </Button>
          ) : null
        }
      />
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Search code or title" allowClear style={{ width: 260 }} onSearch={setSearch} />
          <Select allowClear placeholder="Category" style={{ width: 200 }} value={category} onChange={setCategory} options={COURSE_CATEGORIES.map((c) => ({ value: c, label: c }))} />
        </Space>
        <Table rowKey="id" size="small" loading={isLoading} dataSource={data} columns={columns} pagination={{ defaultPageSize: 20 }} />
      </Card>

      <Modal
        title={editing?.id ? 'Edit training course' : 'New training course'}
        open={!!editing}
        width={680}
        onCancel={() => setEditing(null)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={async (v) => {
            await save.mutateAsync(v);
            setEditing(null);
          }}
        >
          <Row gutter={12}>
            <Col span={8}>
              <Form.Item name="code" label="Code" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item name="title" label="Training" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select options={COURSE_CATEGORIES.map((c) => ({ value: c, label: c }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="default_trainer" label="Default trainer">
                <Input />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="content" label="Training content">
                <Input.TextArea rows={3} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="frequency" label="Frequency">
                <Select options={COURSE_FREQUENCIES.map((c) => ({ value: c, label: c }))} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="frequency_months" label="Repeat every (months)" tooltip="Used for refresher training">
                <InputNumber min={1} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="duration_hours" label="Duration (hours)">
                <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Space wrap>
                <Form.Item name="is_induction" valuePropName="checked" noStyle>
                  <Checkbox>Induction (scheduled automatically for new joiners)</Checkbox>
                </Form.Item>
                <Form.Item name="effectiveness_required" valuePropName="checked" noStyle>
                  <Checkbox>Evaluate effectiveness after training</Checkbox>
                </Form.Item>
                <Form.Item name="is_active" valuePropName="checked" noStyle>
                  <Checkbox>Active</Checkbox>
                </Form.Item>
              </Space>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default TrainingCoursesPage;
